import { getConfig } from '@cian/config/browser';
import { getLogger } from '@cian/logger/browser';

import {
  classifySource,
  getCulprit,
  getEntryType,
  getHeaderHeight,
  getRootHeight,
  getSessionInfo,
  serializeSources,
  type ILayoutShift,
  type IOwnershipSplit,
} from './helpers';

interface IClsDebugParams {
  /** Авторизован ли посетитель: у авторизованных шапка выше и в ней больше блоков. */
  isAuthenticated: boolean;
  /** Идентификатор компании — чтобы найти воспроизводимую страницу по логам. */
  realtyUserId: number | null;
  /** Сколько объявлений на странице: длина контента меняет и вёрстку, и объём гидратации. */
  offersTotal: number | null;
}

/**
 * Временная диагностика CLS: пишет в ELK атрибуцию сдвига по каждой загрузке.
 * Нужна, чтобы понять, что именно двигается, и главное — приходит сдвиг из нашей
 * разметки или из микрофронтендов шапки и футера, которые выкатываются отдельно.
 *
 * Удалить после того, как причина найдена.
 */

/** Доля хороших сессий, которые тоже логируем — чтобы знать распределение, а не только хвост. */
const DEFAULT_GOOD_SAMPLE_RATE = 0.05;

/**
 * Считает сдвиги параллельно с web-vitals: тот отдаёт только крупнейший сдвиг,
 * а нам нужна разбивка по владельцу разметки за всю сессию.
 */
function observeOwnership(rootId: string): IOwnershipSplit {
  const split: IOwnershipSplit = {
    ours: 0,
    rootMoved: 0,
    header: 0,
    footer: 0,
    foreign: 0,
    unknown: 0,
    scrollYAtWorst: 0,
    worstValue: 0,
    worstSources: [],
    headerHeightStart: getHeaderHeight(rootId),
    headerHeightEnd: null,
  };

  const observer = new PerformanceObserver(list => {
    for (const entry of list.getEntries() as ILayoutShift[]) {
      if (entry.hadRecentInput) {
        continue;
      }

      const sources = entry.sources || [];
      const owners = sources.map(source => classifySource(source.node, rootId));

      if (sources.length === 0) {
        split.unknown += entry.value;
      } else if (owners.includes('rootMoved')) {
        /* Корень уехал целиком — причина снаружи, что бы ещё ни было в источниках. */
        split.rootMoved += entry.value;
      } else if (owners.includes('ours')) {
        split.ours += entry.value;
      } else if (owners.includes('header')) {
        split.header += entry.value;
      } else if (owners.includes('footer')) {
        split.footer += entry.value;
      } else {
        split.foreign += entry.value;
      }

      if (entry.value > split.worstValue) {
        split.worstValue = entry.value;
        split.scrollYAtWorst = Math.round(window.scrollY);
        split.worstSources = serializeSources(sources);
      }
    }
  });

  observer.observe({ type: 'layout-shift', buffered: true } as PerformanceObserverInit);

  return split;
}

export function initClsDebug(params: IClsDebugParams): void {
  try {
    const config = getConfig();

    if (config.get<boolean>('companyPage.clsDebug.enabled') !== true) {
      return;
    }

    const goodSampleRate = config.get<number>('companyPage.clsDebug.goodSampleRate') ?? DEFAULT_GOOD_SAMPLE_RATE;
    const rootId = config.getStrict<string>('projectName');

    let maxScrollY = 0;
    const trackScroll = () => {
      const y = Math.round(window.scrollY);
      if (y > maxScrollY) {
        maxScrollY = y;
      }
    };
    window.addEventListener('scroll', trackScroll, { passive: true });

    const split = observeOwnership(rootId);
    const rootHeightStart = getRootHeight(rootId);

    void import('web-vitals/attribution')
      .then(({ onCLS }) => {
        onCLS(metric => {
          try {
            if (metric.rating === 'good' && Math.random() > goodSampleRate) {
              return;
            }

            const attribution = (metric.attribution || {}) as Record<string, unknown>;
            const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
            const round = (value: unknown, digits = 4) =>
              typeof value === 'number' ? Number(value.toFixed(digits)) : null;

            split.headerHeightEnd = getHeaderHeight(rootId);

            const navType = nav ? nav.type : null;

            getLogger().warning('cwv_cls_debug', {
              /* Три поля, ради которых всё затевалось: сколько, какой статус, кто. */
              cls: round(metric.value),
              rating: metric.rating,
              culprit: getCulprit(split),

              /**
               * Готовые категории для фильтрации: числовые сравнения по коллекции
               * key/value в ELK ненадёжны, а по строке фильтр работает всегда.
               */
              entryType: getEntryType(navType),
              viewportBucket: window.innerWidth < 1025 ? 'narrow' : 'wide',

              target: (attribution.largestShiftTarget as string) || null,

              /**
               * Кто именно виноват. `target` выше — это пострадавший, его сдвинули.
               * Виновника ищем здесь по `dh`: у кого изменилась высота, тот и раздвинул вёрстку.
               * У кого только `dy` — того просто отнесло.
               */
              worstSources: split.worstSources,

              /**
               * Схлопнулась ли шапка после гидратации. Если Start и End отличаются —
               * причина снаружи, и никакая правка в нашем репозитории её не уберёт.
               */
              headerHeightStart: split.headerHeightStart,
              headerHeightEnd: split.headerHeightEnd,

              shiftValue: round(attribution.largestShiftValue),
              shiftTime: round(attribution.largestShiftTime, 0),
              loadState: (attribution.loadState as string) || null,
              shifts: metric.entries.length,

              /* Кто двигал вёрстку. rootMoved — корень толкнуло снаружи, мы пострадавший. */
              clsOurs: round(split.ours),
              clsRootMoved: round(split.rootMoved),
              clsHeader: round(split.header),
              clsFooter: round(split.footer),
              clsForeign: round(split.foreign),
              clsUnknown: round(split.unknown),

              /* Где стоял экран в момент худшего сдвига, а не в момент отправки. */
              scrollYAtWorst: split.scrollYAtWorst,
              maxScrollY,
              scrollYNow: Math.round(window.scrollY),

              /* Ширина решает: ui-kit переключает вёрстку на 1025 px. */
              viewportW: window.innerWidth,
              viewportH: window.innerHeight,
              dpr: window.devicePixelRatio,

              /* Наша часть выросла или сжалась — прямое измерение вместо выводов по соседям. */
              rootHeightStart,
              rootHeightEnd: getRootHeight(rootId),

              /**
               * Состояние сессии. Разделяет «медленный канал», «слабое железо»
               * и «залогинен» — три версии, которые иначе не различить.
               */
              isAuthenticated: params.isAuthenticated,
              realtyUserId: params.realtyUserId,
              offersTotal: params.offersTotal,
              ...getSessionInfo(),

              /* Как пришли: якорь и тип навигации объясняют позицию экрана к моменту гидратации. */
              hash: window.location.hash || null,
              path: window.location.pathname,
              navType,
              ttfb: nav ? Math.round(nav.responseStart) : null,
            });
          } catch {
            /* диагностика не должна ломать страницу */
          }
        });
      })
      .catch(() => undefined);
  } catch {
    /* диагностика не должна ломать страницу */
  }
}
